import InitOrder from "@/components/init-order";

import {
  availabilityQueryOptions,
  hasDeliveryShiftsQueryOptions,
  openingHoursQueryOptions,
} from "@/lib/shifts";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { couponsQueryOptions } from "@/lib/coupons";
import { productsQueryOptions } from "@/lib/products";
import { SalesChannels } from "@/lib/storefront/channel";
import { storefrontTagsQueryOptions } from "@/lib/storefront/catalog";
import {
  storefrontSearchSchema,
  type StorefrontSearch,
} from "@/lib/storefront/catalog-filters";
import { StorefrontCatalog } from "@/components/storefront/catalog/storefront-catalog";

// Create a stable date reference to prevent infinite fetching
const TODAY = new Date();

export const Route = createFileRoute("/$locale/")({
  component: HomePage,
  // The webshop keeps its catalogue filters here; the ordering site has none.
  validateSearch: (search) => storefrontSearchSchema.parse(search),
  loader: async ({ context }) => {
    const websiteConfig = await context.queryClient.ensureQueryData(
      websiteConfigQueryOptions()
    );

    // A webshop opens on its catalogue instead of the order type chooser.
    if (websiteConfig.channel === SalesChannels.storefront) {
      return {
        products: context.queryClient.ensureQueryData(productsQueryOptions()),
        tags: context.queryClient.ensureQueryData(storefrontTagsQueryOptions()),
      };
    }

    return {
      availability: context.queryClient.ensureQueryData(
        availabilityQueryOptions(TODAY)
      ),
      hasDeliveryShifts: context.queryClient.ensureQueryData(
        hasDeliveryShiftsQueryOptions()
      ),
      openingHours: context.queryClient.ensureQueryData(
        openingHoursQueryOptions()
      ),
      websiteConfig: context.queryClient.ensureQueryData(
        websiteConfigQueryOptions()
      ),
    };
  },
});

function HomePage() {
  const { data: websiteConfig } = useSuspenseQuery(websiteConfigQueryOptions());

  return websiteConfig.channel === SalesChannels.storefront ? (
    <StorefrontHome
      heroImage={websiteConfig.backgroundImage}
      heroDescription={websiteConfig.heroDescription}
    />
  ) : (
    <OrderingHome />
  );
}

function StorefrontHome({
  heroImage,
  heroDescription,
}: {
  heroImage?: string;
  heroDescription?: string;
}) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleSearchChange = (patch: Partial<StorefrontSearch>) => {
    navigate({
      search: (previous) => ({ ...previous, ...patch }),
      // Typing a search refines the page; every other filter is a step the
      // back button should be able to undo.
      replace: Object.keys(patch).every((key) => key === "q"),
      resetScroll: false,
    });
  };

  return (
    <StorefrontCatalog
      search={search}
      onSearchChange={handleSearchChange}
      heroImage={heroImage}
      heroDescription={heroDescription}
    />
  );
}

function OrderingHome() {
  const availabilityQuery = useSuspenseQuery(availabilityQueryOptions(TODAY));
  const websiteConfigQuery = useSuspenseQuery(websiteConfigQueryOptions());

  const availability = availabilityQuery.data;

  return (
    <div className="flex flex-1 flex-col">
      <div
        className="relative flex-1 flex flex-col justify-center"
        style={{
          backgroundImage: websiteConfigQuery.data.backgroundImage
            ? `url(${websiteConfigQuery.data.backgroundImage})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {websiteConfigQuery.data.backgroundImage && (
          <div className="absolute inset-0 bg-black/50"></div>
        )}

        {websiteConfigQuery.data.heroDescription && (
          <section className="relative bg-gradient-to-b from-primary/10 via-transparent to-transparent">
            <div className="container mx-auto px-4 py-12 text-center">
              {websiteConfigQuery.data.heroDescription && (
                <div
                  className="text-black bg-white border rounded-xl shadow md:w-1/2 w-2/3 mx-auto p-5"
                  dangerouslySetInnerHTML={{
                    __html: websiteConfigQuery.data.heroDescription,
                  }}
                />
              )}
            </div>
          </section>
        )}

        <div className="text-center">
          <InitOrder
            availability={availability.data ?? []}
            closedMessages={availability.closedMessages ?? []}
          />
        </div>
      </div>
    </div>
  );
}
